import type { UploadedFileSummary } from '../types';
import {
  FILE_UPLOAD_CHUNK_SIZE_BYTES,
  fileUploadMaxBytes,
  legacyOfficeSuccessor,
  UPLOAD_ALLOWED_EXTENSIONS,
  UPLOAD_LEGACY_OFFICE_MESSAGE,
  UPLOAD_READ_MESSAGES,
  type UploadEntry,
} from '@realplc/types';
import { apiFetch, ensureAuthToken, createAuthHeaders, refreshTokenIfNeeded } from './auth';

const API_BASE = import.meta.env.VITE_API_URL || '';
const CHUNK_SIZE = FILE_UPLOAD_CHUNK_SIZE_BYTES;
const ALLOWED_EXTENSIONS = new Set<string>(UPLOAD_ALLOWED_EXTENSIONS);
const MAX_409_RETRIES = 3;
const RESUME_STORAGE_KEY_PREFIX = 'file_upload_resume_';

export interface UploadFileOptions {
  sessionId?: string | null;
  projectId?: string | null;
  engineeringLibraryId?: string | null;
  entry?: UploadEntry;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateLocalId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).substring(2, 8);
  return `local_${ts}_${rand}`;
}

function buildAuthHeaders(token: string | null): Record<string, string> {
  return createAuthHeaders(token);
}

async function computeFileSha256(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function simpleUpload(
  file: File,
  options: UploadFileOptions,
  onProgress?: (progress: number) => void,
): Promise<UploadedFileSummary> {
  let token = await ensureAuthToken();

  const doUpload = (authToken: string | null): Promise<UploadedFileSummary> => {
    const headers = { ...buildAuthHeaders(authToken) };

    return new Promise<UploadedFileSummary>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `${API_BASE}/api/v1/files`;

      xhr.open('POST', url);

      // Set auth headers
      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value);
      }

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            onProgress(e.loaded / e.total);
          }
        });
      }

      xhr.addEventListener('load', () => {
        try {
          const payload = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300 && payload.file) {
            resolve(payload.file as UploadedFileSummary);
          } else {
            reject({ status: xhr.status, message: payload.message || `Upload failed with status ${xhr.status}` });
          }
        } catch {
          reject({ status: xhr.status, message: 'Failed to parse upload response' });
        }
      });

      xhr.addEventListener('error', () => reject({ status: 0, message: 'Network error during upload' }));
      xhr.addEventListener('abort', () => reject({ status: 0, message: 'Upload aborted' }));

      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', file.name);
      formData.append('mimeType', file.type || 'application/octet-stream');
      formData.append('sizeBytes', String(file.size));
      if (options.sessionId) {
        formData.append('sessionId', options.sessionId);
      }
      if (options.projectId) {
        formData.append('projectId', options.projectId);
      }
      if (options.engineeringLibraryId) {
        formData.append('engineeringLibraryId', options.engineeringLibraryId);
      }
      if (options.entry) {
        formData.append('entry', options.entry);
      }

      xhr.send(formData);
    });
  };

  try {
    return await doUpload(token);
  } catch (err: any) {
    // On 401, attempt token refresh and retry once
    if (err?.status === 401) {
      const refreshed = await refreshTokenIfNeeded();
      if (refreshed) {
        token = refreshed.token;
        return doUpload(token);
      }
    }
    throw err instanceof Error ? err : new Error(err?.message || 'Upload failed');
  }
}

// ---------------------------------------------------------------------------
// Chunked upload for files > 5 MB
// ---------------------------------------------------------------------------

interface ChunkUploadState {
  uploadId: string;
  fileName: string;
  totalChunks: number;
  uploadedChunks: number[];
  fileSha256: string;
}

export interface UploadedFileParseStatus {
  scanStatus: NonNullable<UploadedFileSummary['scanStatus']>;
  parseStatus: NonNullable<UploadedFileSummary['parseStatus']>;
  conversionStatus: NonNullable<UploadedFileSummary['conversionStatus']>;
  contextStatus?: UploadedFileSummary['contextStatus'];
  contextKind?: UploadedFileSummary['contextKind'];
  contextRole?: UploadedFileSummary['contextRole'];
  sendableReason?: string;
  cardStatus: UploadedFileSummary['cardStatus'];
  warnings?: unknown[];
}

export function deriveUploadedFileCardStatus(status: {
  uploadStatus?: UploadedFileSummary['uploadStatus'];
  scanStatus?: UploadedFileSummary['scanStatus'];
  parseStatus?: UploadedFileSummary['parseStatus'];
  conversionStatus?: UploadedFileSummary['conversionStatus'];
  contextStatus?: UploadedFileSummary['contextStatus'];
  warnings?: unknown[];
}): UploadedFileSummary['cardStatus'] {
  if (status.uploadStatus === 'uploading') return 'uploading';
  if (
    status.uploadStatus === 'failed'
    || status.scanStatus === 'failed'
    || status.parseStatus === 'failed'
    || status.contextStatus === 'failed'
  ) {
    return 'failed';
  }
  if (status.scanStatus === 'infected') return 'failed';
  if (status.scanStatus === 'pending' || status.scanStatus === 'processing') return 'scanning';
  if (status.conversionStatus === 'pending' || status.conversionStatus === 'processing') return 'converting';
  if (status.parseStatus === 'pending' || status.parseStatus === 'processing') return 'processing';
  if (status.contextStatus === 'pending' || status.contextStatus === 'processing') return 'processing';
  if (status.warnings?.length) return 'warning';
  return 'ready';
}

const READ_MESSAGES = new Set<string>(Object.values(UPLOAD_READ_MESSAGES));

/**
 * What the card says about a file that was not read, or not all of it: the
 * sentence the server gave, to be passed through t().
 *
 * A failed file used to say "File parsing is not ready (failed)", and a file
 * read in part "Parsed (compat)", whatever had happened to it. Of a partly read
 * file's warnings only our own sentences are shown: a parsing library's
 * ("Unrecognised paragraph style…") say nothing a user can act on.
 */
export function uploadedFileReadNote(
  file: Pick<UploadedFileSummary, 'cardStatus' | 'sendableReason' | 'parseError' | 'warnings'>,
): string | undefined {
  if (file.cardStatus === 'failed') return file.sendableReason || file.parseError || undefined;
  if (file.cardStatus === 'warning') return file.warnings?.find(message => READ_MESSAGES.has(message));
  return undefined;
}

/**
 * What a file's card says once the file has been read: that it is ready, or
 * the server's sentence on what was not read of it. Not translated. Nothing
 * while the file is still being read.
 */
export function uploadedFileReadResult(
  file: Pick<UploadedFileSummary, 'cardStatus' | 'sendableReason' | 'parseError' | 'warnings'>,
): string | undefined {
  if (file.cardStatus === 'ready') return 'Ready';
  if (file.cardStatus === 'warning') return uploadedFileReadNote(file) || 'Parsed (compat)';
  if (file.cardStatus === 'failed') return uploadedFileReadNote(file) || 'Parse failed';
  return undefined;
}

/**
 * The status endpoint sends each warning as `{ code, message, severity }`; the
 * upload response sends its message. Read with String(), the first became
 * "[object Object]".
 */
export function uploadWarningMessages(warnings: unknown[] | undefined): string[] | undefined {
  return warnings?.map(warning => {
    if (typeof warning === 'string') return warning;
    const message = (warning as { message?: unknown } | null)?.message;
    return typeof message === 'string' ? message : String(warning);
  });
}

export function isUploadedFileReadyForSend(file: UploadedFileSummary): boolean {
  if (file.uploadStatus !== 'uploaded') return false;
  if (file.scanStatus !== 'clean' && file.scanStatus !== 'skipped') return false;
  if (file.parseStatus !== 'ready' && file.parseStatus !== 'not_required') return false;
  if (file.contextStatus && file.contextStatus !== 'ready' && file.contextStatus !== 'not_supported') return false;
  return true;
}

function persistResumeState(state: ChunkUploadState): void {
  try {
    sessionStorage.setItem(
      `${RESUME_STORAGE_KEY_PREFIX}${state.uploadId}`,
      JSON.stringify(state),
    );
  } catch {
    // sessionStorage full or unavailable — resume will not be possible
  }
}

function clearResumeState(uploadId: string): void {
  try {
    sessionStorage.removeItem(`${RESUME_STORAGE_KEY_PREFIX}${uploadId}`);
  } catch {
    // ignore
  }
}

function getResumeStates(): ChunkUploadState[] {
  const states: ChunkUploadState[] = [];
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(RESUME_STORAGE_KEY_PREFIX)) {
        const raw = sessionStorage.getItem(key);
        if (raw) {
          try {
            const parsed = JSON.parse(raw) as ChunkUploadState;
            states.push(parsed);
          } catch {
            // corrupted entry, skip
          }
        }
      }
    }
  } catch {
    // sessionStorage unavailable
  }
  return states;
}

async function uploadChunk(
  uploadId: string,
  chunk: Blob,
  chunkIndex: number,
  totalChunks: number,
  chunkSha256: string,
  token: string,
  retryCount = 0,
): Promise<void> {
  const formData = new FormData();
  formData.append('chunk', chunk);
  formData.append('chunkIndex', String(chunkIndex));
  formData.append('totalChunks', String(totalChunks));
  formData.append('chunkSha256', chunkSha256);

  const headers = { ...buildAuthHeaders(token) };
  delete (headers as Record<string, string>)['Content-Type']; // let browser set multipart boundary

  const response = await fetch(
    `${API_BASE}/api/v1/file-uploads/${uploadId}/chunks`,
    {
      method: 'POST',
      headers,
      body: formData,
    },
  );

  if (response.ok) return;

  if (response.status === 401 && retryCount < 1) {
    // Token expired — refresh and retry once
    const refreshed = await refreshTokenIfNeeded();
    if (refreshed) {
      return uploadChunk(uploadId, chunk, chunkIndex, totalChunks, chunkSha256, refreshed.token, retryCount + 1);
    }
  }

  if (response.status === 409 && retryCount < MAX_409_RETRIES) {
    // Conflict — chunk already uploaded or collision. Retry with backoff.
    const delay = Math.min(1000 * Math.pow(2, retryCount), 8000);
    await new Promise((r) => setTimeout(r, delay));
    return uploadChunk(uploadId, chunk, chunkIndex, totalChunks, chunkSha256, token, retryCount + 1);
  }

  const payload = await response.json().catch(() => ({}));
  throw new Error(
    (payload as { message?: string }).message || `Chunk ${chunkIndex} upload failed with status ${response.status}`,
  );
}

async function uploadLargeFile(
  file: File,
  options: UploadFileOptions,
  onProgress?: (progress: number) => void,
): Promise<UploadedFileSummary> {
  let token = await ensureAuthToken();
  const fileSha256 = await computeFileSha256(file);

  // --- Init (with 401 retry) ---
  const doInit = async (authToken: string | null) => {
    const initHeaders = {
      'Content-Type': 'application/json',
      ...buildAuthHeaders(authToken),
    };
    const initResponse = await fetch(`${API_BASE}/api/v1/file-uploads/init`, {
      method: 'POST',
      headers: initHeaders,
      body: JSON.stringify({
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        fileSha256,
        sessionId: options.sessionId || undefined,
        projectId: options.projectId || undefined,
        engineeringLibraryId: options.engineeringLibraryId || undefined,
        entry: options.entry,
      }),
    });

    if (!initResponse.ok) {
      const initPayload = await initResponse.json().catch(() => ({}));
      throw { status: initResponse.status, message: (initPayload as { message?: string }).message || `Init upload failed with status ${initResponse.status}` };
    }

    return (await initResponse.json()) as { uploadId: string };
  };

  let initData: { uploadId: string };
  try {
    initData = await doInit(token);
  } catch (err: any) {
    if (err?.status === 401) {
      const refreshed = await refreshTokenIfNeeded();
      if (refreshed) {
        token = refreshed.token;
        initData = await doInit(token);
      } else {
        throw err instanceof Error ? err : new Error(err?.message || 'Init upload failed');
      }
    } else {
      throw err instanceof Error ? err : new Error(err?.message || 'Init upload failed');
    }
  }

  const { uploadId } = initData;

  // --- Chunks ---
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const uploadedChunks: number[] = [];
  let bytesSent = 0;

  const resumeState: ChunkUploadState = {
    uploadId,
    fileName: file.name,
    totalChunks,
    uploadedChunks,
    fileSha256,
  };
  persistResumeState(resumeState);

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    // Compute chunk SHA-256
    const chunkBuffer = await chunk.arrayBuffer();
    const chunkHashBuffer = await crypto.subtle.digest('SHA-256', chunkBuffer);
    const chunkHashArray = Array.from(new Uint8Array(chunkHashBuffer));
    const chunkSha256 = chunkHashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    await uploadChunk(uploadId, chunk, i, totalChunks, chunkSha256, token);

    uploadedChunks.push(i);
    bytesSent += chunk.size;
    persistResumeState(resumeState);

    if (onProgress) {
      onProgress(bytesSent / file.size);
    }
  }

  // --- Complete (with 401 retry) ---
  const doComplete = async (authToken: string | null) => {
    const completeHeaders = {
      'Content-Type': 'application/json',
      ...buildAuthHeaders(authToken),
    };
    const completeResponse = await fetch(
      `${API_BASE}/api/v1/file-uploads/${uploadId}/complete`,
      {
        method: 'POST',
        headers: completeHeaders,
        body: JSON.stringify({ fileSha256 }),
      },
    );

    const completePayload = await completeResponse.json().catch(() => ({}));
    if (!completeResponse.ok || !(completePayload as Record<string, unknown>).file) {
      throw { status: completeResponse.status, message: (completePayload as { message?: string }).message || `Complete upload failed with status ${completeResponse.status}` };
    }

    return completePayload as { file: UploadedFileSummary };
  };

  let completeResult: { file: UploadedFileSummary };
  try {
    completeResult = await doComplete(token);
  } catch (err: any) {
    if (err?.status === 401) {
      const refreshed = await refreshTokenIfNeeded();
      if (refreshed) {
        completeResult = await doComplete(refreshed.token);
      } else {
        throw err instanceof Error ? err : new Error(err?.message || 'Complete upload failed');
      }
    } else {
      throw err instanceof Error ? err : new Error(err?.message || 'Complete upload failed');
    }
  }

  clearResumeState(uploadId);

  return completeResult.file;
}

// ---------------------------------------------------------------------------
// Auto-router
// ---------------------------------------------------------------------------

export async function uploadFile(
  file: File,
  optionsOrSessionId?: UploadFileOptions | string | null,
  onProgress?: (progress: number) => void,
): Promise<UploadedFileSummary> {
  const localId = generateLocalId();
  let options: UploadFileOptions;
  if (typeof optionsOrSessionId === 'string' || optionsOrSessionId == null) {
    options = { sessionId: typeof optionsOrSessionId === 'string' ? optionsOrSessionId : undefined };
  } else {
    options = optionsOrSessionId;
  }
  const maxBytes = fileUploadMaxBytes(options.entry);
  if (file.size > maxBytes) {
    throw new Error(`File exceeds ${maxBytes / 1024 / 1024}MB limit`);
  }

  if (file.size <= CHUNK_SIZE) {
    const result = await simpleUpload(file, options, onProgress);
    return { ...result, localId };
  }

  const result = await uploadLargeFile(file, options, onProgress);
  return { ...result, localId };
}

// ---------------------------------------------------------------------------
// Interrupted upload reconciliation.
// Browser sessions cannot recover the original File object after reload, so
// this only finalizes uploads whose chunks already reached the server and
// clears stale local bookkeeping for anything that still needs binary chunks.
// ---------------------------------------------------------------------------

export async function resumeInterruptedUploads(
  onResumed?: (file: UploadedFileSummary) => void,
): Promise<void> {
  const states = getResumeStates();
  const token = await ensureAuthToken();

  for (const state of states) {
    try {
      // Check upload status from server
      const statusHeaders = buildAuthHeaders(token);
      const statusResponse = await fetch(
        `${API_BASE}/api/v1/file-uploads/${state.uploadId}/status`,
        { headers: statusHeaders },
      );

      if (!statusResponse.ok) {
        // Upload no longer valid — clean up
        clearResumeState(state.uploadId);
        continue;
      }

      const statusData = (await statusResponse.json()) as {
        status: string;
        uploadedChunks?: number[];
        totalChunks?: number;
      };

      if (statusData.status === 'completed') {
        // Already completed, clean up local state
        clearResumeState(state.uploadId);
        continue;
      }

      // Determine which chunks are missing
      const serverChunks = new Set(statusData.uploadedChunks || []);
      const missingChunks: number[] = [];
      for (let i = 0; i < state.totalChunks; i++) {
        if (!serverChunks.has(i)) {
          missingChunks.push(i);
        }
      }

      if (missingChunks.length === 0) {
        // All chunks on server — try to complete
        const completeHeaders = {
          'Content-Type': 'application/json',
          ...buildAuthHeaders(token),
        };
        const completeResponse = await fetch(
          `${API_BASE}/api/v1/file-uploads/${state.uploadId}/complete`,
          {
            method: 'POST',
            headers: completeHeaders,
            body: JSON.stringify({ fileSha256: state.fileSha256 }),
          },
        );
        const completePayload = await completeResponse.json().catch(() => ({}));
        if (completeResponse.ok && (completePayload as Record<string, unknown>).file) {
          clearResumeState(state.uploadId);
          onResumed?.((completePayload as { file: UploadedFileSummary }).file);
        } else {
          clearResumeState(state.uploadId);
        }
        continue;
      }

      // Resume missing chunks — we don't have the File object anymore,
      // so we can't resume automatically. Clean up for now.
      // The user will need to re-upload.
      clearResumeState(state.uploadId);
    } catch {
      // Network error — skip this interrupted upload
      clearResumeState(state.uploadId);
    }
  }
}

// ---------------------------------------------------------------------------
// File metadata / preview / deletion
// ---------------------------------------------------------------------------

export async function getUploadedFileParseStatus(fileId: string): Promise<UploadedFileParseStatus> {
  const response = await apiFetch(`${API_BASE}/api/v1/files/${encodeURIComponent(fileId)}/parse-status`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((payload as { message?: string }).message || `Failed to fetch file status (${response.status})`);
  }
  const status = payload as UploadedFileParseStatus;
  return {
    ...status,
    cardStatus: status.cardStatus || deriveUploadedFileCardStatus(status),
  };
}

export async function fetchUploadedFilePreviewBlob(fileId: string): Promise<Blob> {
  const response = await apiFetch(`${API_BASE}/api/v1/files/${encodeURIComponent(fileId)}/preview`);
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error((payload as { message?: string }).message || `Failed to fetch image preview (${response.status})`);
  }
  return response.blob();
}

/**
 * The files a conversation holds: the ones its turns were sent with, less
 * the ones removed since. Every turn that continues it is given all of them.
 */
export async function listConversationFiles(sessionId: string): Promise<UploadedFileSummary[]> {
  const response = await apiFetch(`${API_BASE}/api/v1/files/sessions/${encodeURIComponent(sessionId)}`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((payload as { message?: string }).message || `Failed to list the conversation's files (${response.status})`);
  }
  const files = (payload as { files?: unknown }).files;
  return Array.isArray(files) ? files as UploadedFileSummary[] : [];
}

export async function deleteUploadedFile(fileId: string): Promise<void> {
  const response = await apiFetch(`${API_BASE}/api/v1/files/${encodeURIComponent(fileId)}`, {
    method: 'DELETE',
  });
  if (!response.ok && response.status !== 404) {
    const payload = await response.json().catch(() => ({}));
    throw new Error((payload as { message?: string }).message || `Failed to delete uploaded file (${response.status})`);
  }
}

// ---------------------------------------------------------------------------
// Legacy export for ChatInput backward compat
// ---------------------------------------------------------------------------

export interface RefusedUpload {
  fileName: string;
  reason: 'legacy_office' | 'unsupported' | 'too_large';
}

/**
 * The files of a selection that are not uploaded, each with why.
 *
 * Asked before a byte is sent. An old .doc used to upload whole and then
 * fail to be read, an .xls was "unsupported" with no word on what to do, and
 * a file of any size up to 500 MB went to a gateway that reads each one whole.
 */
export function refusedUploads(
  files: ReadonlyArray<Pick<File, 'name' | 'size'>>,
  entry: UploadEntry,
): RefusedUpload[] {
  const maxBytes = fileUploadMaxBytes(entry);
  const refused: RefusedUpload[] = [];
  for (const file of files) {
    const name = file.name.trim().toLowerCase();
    const dot = name.lastIndexOf('.');
    if (legacyOfficeSuccessor(file.name)) {
      refused.push({ fileName: file.name, reason: 'legacy_office' });
    } else if (!ALLOWED_EXTENSIONS.has(dot >= 0 ? name.slice(dot) : '')) {
      refused.push({ fileName: file.name, reason: 'unsupported' });
    } else if (file.size > maxBytes) {
      refused.push({ fileName: file.name, reason: 'too_large' });
    }
  }
  return refused;
}

/** What the user is told of the files refusedUploads names: which, and what to do about each kind, a line for each. */
export function describeRefusedUploads(
  refused: RefusedUpload[],
  entry: UploadEntry,
  t: (key: string) => string,
): string {
  const named = (reason: RefusedUpload['reason']) => refused
    .filter(file => file.reason === reason)
    .map(file => file.fileName)
    .join(', ');
  const legacy = named('legacy_office');
  const unsupported = named('unsupported');
  const tooLarge = named('too_large');
  return [
    legacy ? `${legacy}: ${t(UPLOAD_LEGACY_OFFICE_MESSAGE)}` : '',
    unsupported ? t('Unsupported file type: {names}').replace('{names}', unsupported) : '',
    tooLarge ? `${tooLarge}: ${t('File exceeds {n}MB').replace('{n}', String(fileUploadMaxBytes(entry) / 1024 / 1024))}` : '',
  ].filter(Boolean).join('\n');
}

/**
 * @deprecated Use `uploadFile(file, sessionId, onProgress)` instead.
 * Kept for backward compatibility during migration.
 */
export async function uploadChatFile(
  file: File,
  optionsOrSessionId?: UploadFileOptions | string | null,
  onProgress?: (progress: number) => void,
): Promise<UploadedFileSummary> {
  return uploadFile(file, optionsOrSessionId, onProgress);
}
